import { PetiteAnnonce } from "../../lib/interfaces/petite-annonce.interface";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { EyeIcon } from "lucide-react";
import { usePetiteAnnonce } from "../../lib/hooks/usePetitesAnnonces";
import { useState } from "react";
import { Skeleton } from "../ui/skeleton";
import dayjs from "dayjs";
import { getCouleurPetiteAnnonce, getEtatPetiteAnnonce } from "../../lib/helpers/petites_annonces_helpers";

interface Props {
  initialPetiteAnnonce: PetiteAnnonce;
}

export default function ShowPetiteAnnonceButton({ initialPetiteAnnonce }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { data } = usePetiteAnnonce(initialPetiteAnnonce.id, {
    enabled: isOpen,
    staleTime: 0,
  });
  const petiteAnnonce = data ?? initialPetiteAnnonce;

  return (
    <div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            icon={<EyeIcon size={16} />}
            variant="outline"
            size="icon"
            aria-label="Consulter"
            className="border-slate-300 bg-white text-slate-700"
          />
        </DialogTrigger>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[560px] sm:rounded-2xl">
          <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5">
            <DialogTitle className="text-xl text-slate-950">Petite annonce</DialogTitle>
            <DialogDescription>Retrouvez ici le contenu de votre petite annonce.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 px-6 py-5 text-sm">
            {/* Etat de l'annonce */}
            <div
              className={`${getCouleurPetiteAnnonce(petiteAnnonce.etat)} w-fit rounded-full px-2.5 py-1 text-xs font-semibold`}
            >
              {getEtatPetiteAnnonce(petiteAnnonce.etat)}
            </div>

            {/* Catégorie */}
            <div>
              <div className="font-semibold text-slate-900">Catégorie</div>
              <div className="mt-1 text-slate-600">{petiteAnnonce.categorie?.nom || "Chargement..."}</div>
            </div>

            {/* Contenu de l'annonce */}
            <div>
              <div className="font-semibold text-slate-900">Contenu</div>
              <div className="mt-2 rounded-xl bg-slate-50 p-4 leading-6 text-slate-600">
                <div>{petiteAnnonce.ligne1}</div>
                {petiteAnnonce.ligne2 && <div>{petiteAnnonce.ligne2}</div>}
                {petiteAnnonce.ligne3 && <div>{petiteAnnonce.ligne3}</div>}
                {petiteAnnonce.ligne4 && <div>{petiteAnnonce.ligne4}</div>}
                {petiteAnnonce.ligne5 && <div>{petiteAnnonce.ligne5}</div>}
                {petiteAnnonce.ligne6 && <div>{petiteAnnonce.ligne6}</div>}
                {petiteAnnonce.ligne7 && <div>{petiteAnnonce.ligne7}</div>}
                {petiteAnnonce.ligne8 && <div>{petiteAnnonce.ligne8}</div>}
                {petiteAnnonce.ligne9 && <div>{petiteAnnonce.ligne9}</div>}
                {petiteAnnonce.ligne10 && <div>{petiteAnnonce.ligne10}</div>}
              </div>
            </div>

            {/* Option tramage */}
            <div className="flex items-center gap-2">
              <div className="font-semibold text-slate-900">Tramage :</div>
              <div className="text-slate-600">{petiteAnnonce.tramage ? "Oui" : "Non"}</div>
            </div>

            {/* Mois de publication */}
            <div>
              <div className="font-semibold text-slate-900">Mois de publication</div>
              <div className="mt-1 text-slate-600">
                {petiteAnnonce.parutions ? (
                  petiteAnnonce.parutions.map(({ mois }) => (
                    <div key={mois}>
                      -{" "}
                      {new Date(mois).getMonth() === 6
                        ? `Juillet/août ${dayjs(mois).year()}`
                        : dayjs(mois).format("MMMM YYYY")}
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
